import { Outlet } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';
import AppContext from '../common/containers/AppContext';
import useGetRequest from '../common/services/useGetRequest';
import RevStatsPageContent from './components/RevStatsPageContent';
import getProducts from './services/data/getProducts';
import getCustomerSummaries from './services/data/getCustomerSummaries';
import AreaTypes from './services/AreaTypes';
import HierarchyType from './components/HierarchyType';

// replace undefined with null
export const normalizeIds = ({ area, suite, product, category }) => ({
    area: area ?? null,
    suite: suite ?? null,
    product: product ?? null,
    category: category ?? null,
});

// get product ids json to label mapping
function getProductCompoundIdToLabelMap(products) {
    return products.reduce((acc, p) => {
        acc[JSON.stringify(normalizeIds(p))] = p;
        return acc;
    }, {});
}

// If the search text is empty, we want to display the top layer to the user
function getDefaultSearchOptions(products) {
    const defaultKeysSet = new Set([
        AreaTypes.COMMERCIAL_CLOUD,
        AreaTypes.CONSULTING,
        AreaTypes.DATA_CLOUD,
        AreaTypes.DEVELOPMENT_CLOUD,
        AreaTypes.QUALITY_CLOUD,
        AreaTypes.SERVICES,
    ]);

    const defaultKeyToProduct = {};
    products.forEach((product) => {
        if (defaultKeysSet.has(product.label)) {
            defaultKeyToProduct[product.label] = product;
        }
    });

    const defaultSearchOptions = [];
    defaultKeysSet.forEach((key) => {
        if (defaultKeyToProduct[key] !== undefined) {
            defaultSearchOptions.push(defaultKeyToProduct[key]);
        }
    });

    return defaultSearchOptions;
}

export default function RevStatsAuthenticatedContainer() {
    const [user, userIsLoading] = useGetRequest('/ui/session/userinfo');
    const [customers, setCustomers] = useState(null);
    const [products, setProducts] = useState([]);
    const [productIdsToLabelMap, setProductIdsToLabelMap] = useState([]);
    const [defaultSearchOptions, setDefaultSearchOptions] = useState([]);
    const [customerPageOrder, setCustomerPageOrder] = useState(null);

    const renderPageContent = useCallback(
        (children) => <RevStatsPageContent>{children}</RevStatsPageContent>,
        [],
    );

    useEffect(() => {
        (async () => {
            const [initialCustomers, initialProducts, servicesProducts] = await Promise.all([
                getCustomerSummaries(),
                getProducts(HierarchyType.BASE),
                getProducts(HierarchyType.SERVICES),
            ]);

            setProducts(initialProducts);
            const customerAreaOrder = [
                'Development Cloud',
                'Quality Cloud',
                'Commercial Cloud',
                'Data Cloud',
                'Services',
                'Consulting',
            ];
            const productMap = new Map(initialProducts.map((item) => [item.label, item]));
            const orderedAreasForCustomer = customerAreaOrder
                .map((label) => productMap.get(label))
                .filter((item) => item != null);
            setCustomerPageOrder(orderedAreasForCustomer);

            const productIdToLabelMap = {
                ...getProductCompoundIdToLabelMap(initialProducts),
                ...getProductCompoundIdToLabelMap(servicesProducts),
            };
            setProductIdsToLabelMap(productIdToLabelMap);
            setCustomers(initialCustomers);
            setDefaultSearchOptions(getDefaultSearchOptions(initialProducts));
        })();
    }, []);

    const appContext = useMemo(() => {
        return {
            products,
            productIdsToLabelMap,
            customers,
            defaultSearchOptions,
            renderPageContent,
            user,
            customerPageOrder,
        };
    }, [
        products,
        productIdsToLabelMap,
        customers,
        defaultSearchOptions,
        renderPageContent,
        user,
        customerPageOrder,
    ]);

    if (userIsLoading) {
        return null;
    }

    // Outlet will serve any content that a nested <Route /> matches.
    return (
        <AppContext.Provider value={appContext}>
            <Outlet />
        </AppContext.Provider>
    );
}
